import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from '@apollo/client';
import FileDownload from 'js-file-download';
import Notes from './Form/Notes';
import AGENTS_QUERIES from '../graphql/agents/queries';
import AUTH_QUERIES from '../graphql/auth/queries';
import AUTH_MUTATIONS from '../graphql/auth/mutations';
import INSTALLERS_QUERIES from '../graphql/installers/queries';
import { axiosInstance, get_file_name } from '../shared/utils/axios';
import { Input, Select } from 'antd';
import { colors } from '../config/constants';
import InstallModal from '../shared/components/InstallModal';
import GreaseModal from '../shared/components/GreaseModal';
import BaconButton from '../shared/components/Button';

const { Option } = Select;

const ScreenContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: nowrap;
  min-height: 100vh;
`;

const FormContainer = styled.div`
  flex-grow: 4;
  display: flex;
  flex-direction: column;
  padding-left: 3vw;
  margin: 50px 0px;
`;

const FormHeading = styled.h1`
  font-family: LexiaAd;
`;

const FormSubheading = styled.div`
  display: flex;
  width: 80%;
  margin-bottom: 15px;
  text-align: justify;
  text-justify: inter-word;
`;

const BottomText = styled.div`
  display: flex;
  margin-bottom: 2vh;
  font-size: 12px;
  align-self: stretch;
  color: ${colors.COLOR_GRAY_TEXT};
`;

const FormTextSeparator = styled.div`
  flex-grow: 4;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const InstructionsContent = styled.div`
  font-size:0.9em;
  white-space: pre-line;
`;

const BelowInputsContainer = styled.div`
display: flex;
justify-content: flex-end;
margin-right: 18.2%;
flex-direction: row;
align-items: center;
color: ${colors.COLOR_BACON_SAYS_TEXT}
`;

const FormScreen = () => {

  // Form
  const storedFQDN = JSON.parse(localStorage.getItem('user.organization'))?.fqdn;
  const [fqdn, setFQDN] = useState(storedFQDN || '');
  const [selectedOS, setSelectedOS] = useState();
  const [osList, setOSList] = useState([]);
  const [version, setVersion] = useState(JSON.parse(localStorage.getItem('user.organization'))?.server_version);
  const [versionBE, setVersionBE] = useState([]);

  const shortFQDN = fqdn ? fqdn.replaceAll('.', '').substring(0,8) : '';
  const [downloadErrorMessage, setDownloadErrorMessage] = useState(null);
  const [organizationUpdated, setOrganizationUpdated] = useState(false);

  const { data: organizationData } = useQuery(AUTH_QUERIES.AUTH_ORGANIZATION, {
    fetchPolicy: 'network-only',
  });

  if (!organizationUpdated && organizationData) {
    setOrganizationUpdated(true);
    let organization = organizationData.getOrganizationResponse[0];
    localStorage.setItem('user.organization', JSON.stringify(organization || []));
    setFQDN(organization.fqdn);
  }

  const [editOrganizationField] = useMutation(AUTH_MUTATIONS.AUTH_UPDATE_ORGANIZATION);

  const updateOrganizationField = (field, value) => {
    const organizationId = JSON.parse(localStorage.getItem('user.organization')).id;

    var bodyObj = new Object();
    bodyObj[field] = value;

    editOrganizationField({
      variables: {
        organizationId,
        body: bodyObj
      },
    })
      .then(response => {
        let organization = response.data.updateOrganization;
        localStorage.setItem('user.organization', JSON.stringify(organization || []));
        setFQDN(organization.fqdn);
      });

  };


  // Notes
  const noteMapper = {
    version: {
      text: 'Please select the version of Bacon. The Bacon Agent installers should match for best results. Using earlier version installers may work, but may be unsupported.',
      gif: 'version',
    },
    fqdn: {
      text: 'The Fully Qualified Domain Name (FQDN) of the Bacon Server. This is the same value set when installing/creating Bacon installer.',
      gif: 'fqdn',
    },
    os: {
      text: 'Please select all the available operating systems within your network, that Bacon should manage. Please note, not all operating systems are supported.',
      gif: 'agentOs',
    },
    default: {
      text: [
        {
          title: 'Bacon Agent Installation Instructions:',
        },
        {
          title: 'CentOS:',
        },
        <InstructionsContent key="centos">
          {`1. Unzip the downloaded file.
        2. Run sudo bash Bacon_Agent_RHEL-CentOS_Installer_${shortFQDN}.sh
        `}
        </InstructionsContent>,
        {
          title: 'Ubuntu / Debian:',
        },
        <InstructionsContent key="ubuntu">
          {`1. Unzip the downloaded file.
        2. Run sudo bash Bacon_Agent_Deb-Ubuntu_Installer_${shortFQDN}.sh
        `}
        </InstructionsContent>,
        {
          title: 'Windows',
        },
        <InstructionsContent key="windows">
          {`1. Unzip the downloaded file.
        2. Open a terminal with "Run as administrator" permissions.
        3. Run Bacon_Agent_Windows_Installer_${shortFQDN}.bat
        `}
        </InstructionsContent>,
        {
          title: 'MacOS',
        },
        <InstructionsContent key="macos">
          {`1. Unzip the downloaded file.
          2. Run sudo bash Bacon_Agent_MacOS_Installer_${shortFQDN}.sh
          `}
        </InstructionsContent>
      ],
    }
  };

  const [notes, setNotes] = useState(noteMapper.default.text);
  const [gif, setGif] = useState(undefined);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [modalStatus, setModalStatus] = useState('loading');

  const openModal = () => {
    setModalStatus('loading');
    setShowModal(true);
    setLoadingModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Queries
  useQuery(AGENTS_QUERIES.AGENTS_GET_OS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setOSList(
        data?.getOsResponse?.map(optionBE => ({
          value: optionBE.id,
          text: optionBE.name,
        }))
      );
    },
  });

  useQuery(INSTALLERS_QUERIES.INSTALLERS_GET_VERSIONS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const allVersions =
        data?.getVersionsResponse?.map(optionBE => ({
          value: optionBE.id,
          text: optionBE.friendly_name,
        }));
      setVersionBE(allVersions);
    },
  });

  const onDownloadHandler = () => {
    openModal();
    let axios_calls = [];
    for (let o in selectedOS) {
      const promise = axiosInstance.post('/agents/download_agent/', {
        fqdn,
        os: selectedOS[o],
        server_version: version
      }, { responseType: 'blob' });
      axios_calls.push(promise);
    }
    Promise.all(axios_calls).then(function (values) {
      setLoadingModal(false);
      setModalStatus('complete');
      for (let val in values) {
        FileDownload(values[val].data, get_file_name(values[val].headers));
      }
    }).catch(function () {
      setDownloadErrorMessage('There was an error processing the request, the Server FQDN may be invalid.');
      setLoadingModal(false);
      setModalStatus('error');
    });
  };

  const isUndefined = u => (typeof u) === 'undefined';

  const disabled = isUndefined(fqdn) || isUndefined(selectedOS) || selectedOS.length == 0;

  const onHoverHandler = e => {
    setNotes(noteMapper[e].text);
    setGif(noteMapper[e].gif);
  };

  const onUnhoverHandler = () => {
    setNotes(noteMapper.default.text);
    setGif(undefined);
  };

  return (
    <>
      <ScreenContainer>
        <FormContainer>
          <FormTextSeparator>
            <FormContent>
              <FormHeading>
                Required Information
              </FormHeading>
              <FormSubheading>The place to download any Bacon agents needed for your network. Enter the Bacon Server product version, Bacon server FQDN, and select all relevant operating systems. Bacon agents presented will be relevant to the Bacon server product version selected here.</FormSubheading>
              <Select
                placeholder='Bacon Server version'
                allowClear
                onChange={e => {
                  setVersion(e);
                  updateOrganizationField('server_version', e);
                }}
                onMouseEnter={() => onHoverHandler('version')}
                onMouseLeave={onUnhoverHandler}
                value={version}
              >
                {versionBE.map(os => <Option key={os.value} value={os.value}>{os.text}</Option>)}
              </Select>
              <Input
                placeholder='Bacon Server FQDN'
                value={fqdn}
                onChange={e => setFQDN(e.target.value)}
                onMouseEnter={() => onHoverHandler('fqdn')}
                onMouseLeave={onUnhoverHandler}
                onBlur={e => updateOrganizationField('fqdn', e.target.value)}
              />
              <Select
                placeholder='Endpoints Operating Systems'
                allowClear
                showArrow
                mode='multiple'
                value={selectedOS}
                onChange={e => setSelectedOS(e)}
                onMouseEnter={() => onHoverHandler('os')}
                onMouseLeave={onUnhoverHandler}
                className="notransition"
              >
                {osList.map(os => <Option key={os.value} value={os.value}>{os.text}</Option>)}
              </Select>
              <BelowInputsContainer>
                <BaconButton
                  disabled={disabled}
                  text="Download"
                  onClick={onDownloadHandler}>
                </BaconButton>
              </BelowInputsContainer>
            </FormContent>
            <BottomText>
              Don’t forget to verify all of the information above for a successful installation!
            </BottomText>
          </FormTextSeparator>
        </FormContainer>

        <Notes
          gif={gif}
          notes={notes}
        />

      </ScreenContainer>
      <GreaseModal maskClosable={false} width={1000} title="" visible={showModal} loading={loadingModal}
        onOk={closeModal} closable={false} keyboard={false} showCancelButton={false} >
        <InstallModal longBuild={false} type="server" status={modalStatus} errorMessage={downloadErrorMessage}/>
      </GreaseModal>
    </>);
};

export default FormScreen;